import React from 'react'
import { connect } from 'react-redux'
import { getSelectedStore } from 'x/redux/selectors'
import dayjs from 'dayjs'

import { FlatList, ListRenderItemInfo, RefreshControl, TouchableOpacity } from 'react-native'

import * as util from 'x/utils/util'
import * as NavActions from 'x/utils/navigation'
import time from 'x/utils/time'
import p from 'x/config/platform-specific'
import CONS from 'x/config/constants'
import { fetchErpDocumentList, fetchErpDocumentProducts, syncErpDocuments } from 'x/utils/api/erp'

import VStack from 'xui/components/VStack'
import HStack from 'xui/components/HStack'
import XText from 'xui/components/XText'
import XContainer from 'xui/components/XContainer'
import XCustomHeader from 'xui/components/XCustomHeader'
import XInput from 'xui/components/XInput'
import XSpinner from 'xui/components/XSpinner'
import XIcon from 'xui/components/XIcon'
import XButton from 'xui/components/XButton'
import XCard from 'xui/components/XCard'
import HelpButton from 'xui/components/HelpButton'

import {
  IErpAddStockFromDocViewProps,
  IErpAddStockFromDocViewState,
  IErpDocumentListItem,
  IERPChannel,
  IErpDocQtyAdjustmentHistory,
  IErpDocumentProductItem,
  IErpDocStatus,
} from 'x/index'
import { getErpDocStatusDisplay } from 'x/config/erp-constants'
import { DEPOSIT_FROM_ERP_DOC_REASON } from '../inventory/QtyAdjustmentView'
import OverlaySetupSyncErpDocument, { IOverlaySetupSyncErpDocumentCallBack } from './OverlaySetupSyncErpDocument'
import Box from 'xui/components/Box'

const FETCH_LIMIT = 20
const SEARCH_DEBOUNCE_MS = 800

// Doc types ที่รองรับสำหรับเติมสต๊อก
const ADD_STOCK_DOC_TYPE_OPTIONS: { id: number; label: string }[] = [
  { id: 21, label: 'ใบสั่งซื้อ' },
  { id: 31, label: 'บันทึกซื้อสินค้า' },
]

const TAB_OPTIONS = ['ยังไม่เคยเติมสต๊อก', 'เคยเติมสต๊อกแล้ว']

class ErpAddStockFromDocView extends React.Component<IErpAddStockFromDocViewProps, IErpAddStockFromDocViewState> {
  static displayName = 'ErpAddStockFromDocView'

  isFetching?: boolean

  searchTimeoutId?: ReturnType<typeof setTimeout>

  constructor(props: IErpAddStockFromDocViewProps) {
    super(props)

    const erpChannel = util.getNavParam(props, 'erpChannel') as IERPChannel
    // Derive enabled doc types from the new PO/Expense fields
    const enabledDocTypes: number[] = []
    if (erpChannel?.add_stock_from_po && erpChannel.add_stock_from_po.length > 0) {
      enabledDocTypes.push(21) // PO
    }
    if (erpChannel?.add_stock_from_expense && erpChannel.add_stock_from_expense.length > 0) {
      enabledDocTypes.push(31) // Expense
    }

    this.state = {
      isInitialized: false,
      isRefreshing: false,
      isFetchingMore: false,
      isReachEnd: false,
      isSyncing: false,
      isSyncOverlayVisible: false,
      fetchError: undefined,

      tabIndex: 0,
      searchText: '',
      selectedDocTypes: enabledDocTypes.length > 0 ? [...enabledDocTypes] : [],

      documents: [],
      totalCount: 0,
      lastSyncedAt: undefined,
    }
  }

  async componentDidMount() {
    await util.setStatePromise(this, { isInitialized: true })
    await this._onRefresh()
  }

  componentWillUnmount() {
    if (this.searchTimeoutId) {
      clearTimeout(this.searchTimeoutId)
    }
  }

  _getStoreId = (): number => {
    const { selectedStore } = this.props
    const navStoreId = util.getNavParam(this.props, 'store_id')
    return navStoreId || selectedStore.get('id')
  }

  _getErpChId = (): number => util.getNavParam(this.props, 'erp_ch_id')

  _getErpChannel = (): IERPChannel => util.getNavParam(this.props, 'erpChannel')

  _isAddStockEnabled = (): boolean => {
    const erpChannel = this._getErpChannel()
    const hasPo = erpChannel?.add_stock_from_po && erpChannel.add_stock_from_po.length > 0
    const hasExpense = erpChannel?.add_stock_from_expense && erpChannel.add_stock_from_expense.length > 0
    return hasPo || hasExpense
  }

  _doFetch = async (offset = 0, limit = FETCH_LIMIT): Promise<boolean> => {
    const { documents, tabIndex, searchText, selectedDocTypes } = this.state
    const store_id = this._getStoreId()
    const erp_ch_id = this._getErpChId()

    const hasQtyAdjustment = tabIndex === 1 // tab 1 = เคยเติมสต๊อกแล้ว

    try {
      const res = await fetchErpDocumentList({
        store_id,
        erp_ch_id,
        offset,
        limit,
        erp_doc_types: selectedDocTypes.length > 0 ? selectedDocTypes : undefined,
        erp_doc_code: searchText.trim() || undefined,
        has_qty_adjustment: hasQtyAdjustment,
      })

      const newState: Partial<IErpAddStockFromDocViewState> = {
        fetchError: undefined, // Clear error on success
      }

      if (res && res.documents) {
        // === MOCK: inject status ลงใน documents (หลังบ้านยังไม่ส่ง status มา — ลบ block นี้เมื่อหลังบ้านพร้อม) ===
        const MOCK_STATUSES: IErpDocStatus[] = ['AWAIT_PAYMENT', 'GOT_RECEIPT', 'DRAFT', 'PAID', 'OVERDUE', 'VOIDED']
        const mockDocuments = res.documents.map((doc: IErpDocumentListItem) => ({
          ...doc,
          status: doc.status || MOCK_STATUSES[Math.floor(Math.random() * MOCK_STATUSES.length)],
        }))
        // === END MOCK ===

        if (offset === 0) {
          newState.documents = mockDocuments
        } else {
          newState.documents = documents.concat(mockDocuments)
        }

        if (res.total_count !== undefined) {
          newState.totalCount = res.total_count
        }

        if (!res.has_next_page || res.documents.length < limit) {
          newState.isReachEnd = true
        }
      }

      await util.setStatePromise(this, newState)
      return true
    } catch (err) {
      console.log('_doFetch err => ', err)
      const errorMessage = err instanceof Error ? err.message : 'ไม่สามารถดึงรายการเอกสารได้ กรุณาลองใหม่อีกครั้ง'
      await util.setStatePromise(this, { fetchError: errorMessage })
      return false
    }
  }

  _onRefresh = async () => {
    await this._loadInit()
  }

  _loadInit = async () => {
    if (this.isFetching) {
      return
    }
    this.isFetching = true
    await util.setStatePromise(this, { isRefreshing: true, isReachEnd: false, documents: [], fetchError: undefined })
    await this._doFetch(0)
    await util.setStatePromise(this, { isRefreshing: false })
    this.isFetching = false
  }

  _loadMore = async () => {
    const { documents, isReachEnd, isFetchingMore, fetchError } = this.state

    // Don't try to load more if there's an error
    if (this.isFetching || isReachEnd || isFetchingMore || fetchError) {
      return
    }

    this.isFetching = true
    await util.setStatePromise(this, { isFetchingMore: true })

    const offset = documents.length
    await this._doFetch(offset)

    await util.setStatePromise(this, { isFetchingMore: false })
    this.isFetching = false
  }

  _onEndReached = () => {
    void this._loadMore()
  }

  _onSearchChangeText = (text: string) => {
    if (this.searchTimeoutId) {
      clearTimeout(this.searchTimeoutId)
    }

    this.setState({ searchText: text })

    this.searchTimeoutId = setTimeout(() => {
      void this._onRefresh()
    }, SEARCH_DEBOUNCE_MS)
  }

  _onClearSearch = () => {
    this.setState({ searchText: '' }, () => {
      void this._onRefresh()
    })
  }

  _onTabChange = async (index: number) => {
    await util.setStatePromise(this, { tabIndex: index })
    await this._onRefresh()
  }

  _onToggleDocType = async (docTypeId: number) => {
    const { selectedDocTypes } = this.state
    let newSelectedDocTypes: number[]

    if (selectedDocTypes.includes(docTypeId)) {
      newSelectedDocTypes = selectedDocTypes.filter((id: number) => id !== docTypeId)
    } else {
      newSelectedDocTypes = [...selectedDocTypes, docTypeId]
    }

    await util.setStatePromise(this, { selectedDocTypes: newSelectedDocTypes })
    await this._onRefresh()
  }

  _getEnabledDocTypes = (): number[] => {
    const erpChannel = this._getErpChannel()
    const docTypes: number[] = []
    if (erpChannel?.add_stock_from_po && erpChannel.add_stock_from_po.length > 0) {
      docTypes.push(21) // PO / ใบสั่งซื้อ
    }
    if (erpChannel?.add_stock_from_expense && erpChannel.add_stock_from_expense.length > 0) {
      docTypes.push(31) // Expense / บันทึกซื้อสินค้า
    }
    return docTypes
  }

  _onSync = () => {
    this.setState({ isSyncOverlayVisible: true })
  }

  _onCloseSyncOverlay = () => {
    this.setState({ isSyncOverlayVisible: false })
  }

  _onSyncSubmit = async (callBack: IOverlaySetupSyncErpDocumentCallBack) => {
    const { isSyncing } = this.state
    if (isSyncing) {
      return
    }

    // ปิด overlay ก่อน
    this.setState({ isSyncOverlayVisible: false })

    const store_id = this._getStoreId()
    const erp_ch_id = this._getErpChId()
    const { navigation } = this.props

    await util.setStatePromise(this, { isSyncing: true })

    try {
      // เช็ค BG job ที่กำลัง sync อยู่ก่อน
      const bgJobType = CONS.BACKGROUND_JOB_TYPES.ERP_SYNC_DOCUMENT
      const { isAvailable, bgJobResponse } = await util.checkAvailableBackgroundJobType({
        store_id,
        bgJobType,
      })

      if (!isAvailable) {
        // มี job กำลังทำงานอยู่ — นำทางไปดูคิวคำสั่งงาน
        navigation.dispatch(
          NavActions.navToBackgroundJobInProcessByTypeView({
            storeId: store_id,
            bgJobType,
            initBgJobResponse: bgJobResponse,
            warningText: 'มีคิวซิงก์เอกสารกำลังดำเนินการอยู่ กรุณารอให้เสร็จก่อนเพิ่มคิวถัดไป',
          })
        )
        await util.setStatePromise(this, { isSyncing: false })
        return
      }

      // สั่ง sync พร้อม date params + erp_doc_types
      const enabledDocTypes = this._getEnabledDocTypes()
      const res = await syncErpDocuments({
        store_id,
        erp_ch_id,
        created_at_from: callBack.dateFrom,
        created_at_to: callBack.dateTo,
        erp_doc_types: enabledDocTypes.length > 0 ? enabledDocTypes : undefined,
      })

      if (res?.last_synced_at) {
        await util.setStatePromise(this, { lastSyncedAt: res.last_synced_at })
      }

      // ถ้าได้ bgjob_uuid กลับมา — แสดง alert และนำทางไปดู BG job list
      if (res?.bgjob_uuid) {
        await util.alertWithNavToBgJobListView({
          title: 'สร้าง "คิวคำสั่งงาน" สำเร็จ',
          message: 'คิวคำสั่งงาน สำหรับการ "ซิงก์เอกสารกับระบบบัญชี" ได้ถูกสร้างแล้ว ระบบกำลังดำเนินการอยู่เบื้องหลัง และอาจใช้เวลาซักครู่',
          dispatch: navigation.dispatch,
          selectedStoreId: store_id,
          initBgJobUUID: res.bgjob_uuid,
          onUserConfirm: () => {
            this._onRefresh()
          },
        })
      } else {
        // Fallback: sync สำเร็จแบบไม่ได้ bgjob (กรณี mock หรือ sync เร็ว)
        await this._onRefresh()
      }
    } catch (err) {
      console.log('_onSyncSubmit err => ', err)
    }

    await util.setStatePromise(this, { isSyncing: false })
  }

  _onAddStock = async (doc: IErpDocumentListItem) => {
    const { navigation } = this.props
    const store_id = this._getStoreId()
    const erp_ch_id = this._getErpChId()
    const erpChannel = this._getErpChannel()
    const onStockAdjusted = util.getNavParam(this.props, 'onStockAdjusted')

    try {
      // Fetch products from document
      const res = await fetchErpDocumentProducts({
        erp_doc_id: doc.erp_doc_id,
        store_id,
        erp_ch_id,
        product_code_source: erpChannel.product_code_source,
      })

      if (!res?.products || res.products.length === 0) {
        p.op.showConfirmationOkOnly('', 'ไม่พบสินค้าในเอกสารนี้')
        return
      }

      // Convert to ISearchProductItem format with qty
      // Note: This requires fetching product details - for now we'll pass pp_ids and qty
      const initialPpQtyChanged: { [ppId: number]: number } = {}
      res.products.forEach((product: IErpDocumentProductItem) => {
        initialPpQtyChanged[product.pp_id] = product.qty
      })

      // Navigate to QtyAdjustmentView with prefilled data
      navigation.dispatch(
        NavActions.navToQtyAdjustmentView({
          store_id,
          mode: 'add',
          title: 'เพิ่มจำนวนสินค้าจากเอกสาร',
          confirmationTitle: 'บันทึกการเติมสต๊อกจากเอกสาร',
          colorScheme: 'success',
          initialExtraData: {
            refCode: doc.erp_doc_code,
            note: `เติมสต๊อกจากเอกสาร ${doc.erp_doc_code}`,
            selectedReason: DEPOSIT_FROM_ERP_DOC_REASON,
            erpDocumentId: doc.erp_doc_id,
          },
          onSubmitSuccess: () => {
            // Refresh list when stock adjustment is successful
            this._onRefresh()
            if (onStockAdjusted) {
              onStockAdjusted()
            }
          },
        })
      )
    } catch (err) {
      console.log('_onAddStock err => ', err)
    }
  }

  _navToQtyAdjustmentDetailView = (historyId: number) => {
    const { navigation } = this.props
    const store_id = this._getStoreId()

    navigation.dispatch(
      NavActions.navToQtyAdjustmentDetailView({
        store_id,
        qty_adjustment_history_id: historyId,
      })
    )
  }

  _navToSettings = () => {
    const { navigation } = this.props
    const erp_ch_id = this._getErpChId()
    const store_id = this._getStoreId()

    navigation.dispatch(
      NavActions.navToERPDetailView({
        store_id,
        erp_ch_id,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        callBackUpdateNewErp: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        callBackUpdateNewErpList: () => {},
      })
    )
  }

  _goBack = () => {
    util.navGoBack(this.props)
  }

  _getDocTypeName = (docType: number): string => {
    const option = ADD_STOCK_DOC_TYPE_OPTIONS.find((opt) => opt.id === docType)
    return option?.label || `ประเภท ${docType}`
  }

  _getStatusDisplay = (status?: string) => getErpDocStatusDisplay(status)

  // ===== Render Methods =====

  _renderCustomHeader = () => <XCustomHeader title='เติมสต๊อกจากเอกสาร' headerLeftProps={{ backIcon: true, onPressItem: this._goBack }} />

  _renderNotEnabledMessage = () => {
    const erpChannel = this._getErpChannel()
    const systemName = util.getErpChannelSystemName(erpChannel?.erp_system_id)

    return (
      <VStack flex={1} w='full' p='4' alignItems='center' justifyContent='center' space='4'>
        <XIcon name='alert-circle-outline' family='Ionicons' size='4xl' color='muted.400' />
        <XText variant='inactive' textAlign='center'>
          {`กรุณาเปิดการใช้งาน "เติมสต๊อกจากเอกสาร" \nในหน้าตั้งค่า ${systemName} ก่อน`}
        </XText>
        <XButton onPress={this._navToSettings} colorScheme='primary'>
          ตั้งค่า
        </XButton>
      </VStack>
    )
  }

  _renderSyncSection = () => {
    const { isSyncing, lastSyncedAt } = this.state
    const syncText = lastSyncedAt ? time.convertServerDateTimeToReadableDateTimeText(lastSyncedAt) : 'ยังไม่เคยซิงก์'

    return (
      <VStack w='full' px='2' py='0.5' bg='white'>
        <XCard w='full' p='2'>
          <HStack w='full' alignItems='center' justifyContent='space-between'>
            <VStack flex={1} space='1'>
              <HStack alignItems='center' space='1'>
                <XText bold>ซิงก์เอกสาร</XText>
                <HelpButton onPress={() => p.op.showConfirmationOkOnly('ซิงก์เอกสาร', 'ดึงรายการเอกสารล่าสุดจากระบบ ERP')} />
              </HStack>
              <HStack alignItems='center' space='1'>
                <XText variant='inactive' fontSize='xs'>
                  อัพเดทเมื่อ
                </XText>
                <XText fontSize='xs'>{syncText}</XText>
              </HStack>
            </VStack>
            <XButton onPress={this._onSync} isLoading={isSyncing} variant='outline' leftIcon={<XIcon name='sync' family='Ionicons' />}>
              ซิงก์
            </XButton>
          </HStack>
        </XCard>
      </VStack>
    )
  }

  _renderSearchBar = () => {
    const { searchText } = this.state

    return (
      <HStack w='full' px='2' py='2' space='2' bg='white'>
        <HStack w='full' alignItems='center' bg='primary.100' borderRadius='lg' px='2'>
          <XIcon name='search' family='FontAwesome' size='sm' color='muted.400' />
          <XInput flex={1} placeholder='ค้นหาด้วยชื่อเอกสาร เช่น RT2026050012' value={searchText} onChangeText={this._onSearchChangeText} />
          {searchText.length > 0 ? (
            <TouchableOpacity onPress={this._onClearSearch}>
              <XIcon name='close-circle' family='Ionicons' size='sm' color='muted.400' />
            </TouchableOpacity>
          ) : null}
        </HStack>
      </HStack>
    )
  }

  _renderTabs = () => {
    const { tabIndex } = this.state

    return (
      <HStack w='full' borderBottomWidth={1} borderBottomColor='muted.200'>
        {TAB_OPTIONS.map((tab, index) => {
          const isActive = tabIndex === index
          return (
            <TouchableOpacity key={tab} style={{ flex: 1 }} onPress={() => this._onTabChange(index)}>
              <VStack
                py='3'
                alignItems='center'
                justifyContent='center'
                borderBottomWidth={isActive ? 2 : 0}
                borderBottomColor={isActive ? 'primary.500' : 'transparent'}
                bg={isActive ? 'primary.50' : 'white'}
              >
                <XText bold={isActive} color={isActive ? 'primary.500' : 'muted.500'}>
                  {tab}
                </XText>
              </VStack>
            </TouchableOpacity>
          )
        })}
      </HStack>
    )
  }

  _renderDocTypeFilters = () => {
    const { selectedDocTypes } = this.state
    const erpChannel = this._getErpChannel()
    // Derive enabled doc types from new PO/Expense fields
    const enabledDocTypes: number[] = []
    if (erpChannel?.add_stock_from_po && erpChannel.add_stock_from_po.length > 0) {
      enabledDocTypes.push(21)
    }
    if (erpChannel?.add_stock_from_expense && erpChannel.add_stock_from_expense.length > 0) {
      enabledDocTypes.push(31)
    }

    // Only show filters if more than one doc type is enabled
    if (enabledDocTypes.length <= 1) {
      return null
    }

    return (
      <HStack w='full' px='2' py='2' space='2' bg='white' borderBottomWidth={1} borderBottomColor='muted.100'>
        {enabledDocTypes.map((docTypeId: number) => {
          const isSelected = selectedDocTypes.includes(docTypeId)
          const label = this._getDocTypeName(docTypeId)

          return (
            <TouchableOpacity key={docTypeId} onPress={() => this._onToggleDocType(docTypeId)}>
              <HStack
                px='3'
                py='1.5'
                borderRadius='full'
                borderWidth={1}
                borderColor={isSelected ? 'primary.500' : 'muted.300'}
                bg={isSelected ? 'primary.50' : 'white'}
                alignItems='center'
                space='1'
              >
                {isSelected && <XIcon name='check' family='FontAwesome' size='xs' color='primary.500' />}
                <XText fontSize='sm' color={isSelected ? 'primary.500' : 'muted.600'}>
                  {label}
                </XText>
              </HStack>
            </TouchableOpacity>
          )
        })}
      </HStack>
    )
  }

  _renderListItem = (info: ListRenderItemInfo<IErpDocumentListItem>) => {
    const { item, index } = info
    const { tabIndex } = this.state

    const isOdd = index % 2 !== 0
    const issuedDateText = item.issued_date ? dayjs(item.issued_date).format('D/MM/YYYY') : '-'
    const updatedAtText = item.updated_at ? time.convertServerDateTimeToReadableDateTimeText(item.updated_at) : '-'
    const hasQtyAdjustment = tabIndex === 1

    return (
      <XCard mx='2' my='1' p='3'>
        <VStack w='full' space='2'>
          {/* Header: Doc code and type + status */}
          <HStack w='full' alignItems='center' justifyContent='space-between'>
            <XText bold fontSize='md' flex={1} numberOfLines={1}>
              {item.erp_doc_code}
            </XText>
            <HStack space='1.5' alignItems='center'>
              {this._getStatusDisplay(item.status) ? (
                <HStack px='2' py='0.5' borderRadius='md' bg={this._getStatusDisplay(item.status).bg}>
                  <XText fontSize='xs' color={this._getStatusDisplay(item.status).color}>
                    {this._getStatusDisplay(item.status).label}
                  </XText>
                </HStack>
              ) : null}
              <HStack px='2' py='0.5' borderRadius='md' bg='muted.200'>
                <XText fontSize='xs' variant='inactive'>
                  {this._getDocTypeName(item.erp_doc_type)}
                </XText>
              </HStack>
            </HStack>
          </HStack>

          {/* Info rows */}
          <HStack w='full' space='4'>
            <VStack space='1'>
              <XText variant='inactive' fontSize='xs'>
                วันที่เอกสาร
              </XText>
              <XText fontSize='sm'>{issuedDateText}</XText>
            </VStack>
            <VStack space='1'>
              <XText variant='inactive' fontSize='xs'>
                จำนวนรวม
              </XText>
              <XText fontSize='sm'>{item.sum_qty} ชิ้น</XText>
            </VStack>
            <VStack space='1'>
              <XText variant='inactive' fontSize='xs'>
                สินค้า
              </XText>
              <XText fontSize='sm'>{item.sum_pp_count} รายการ</XText>
            </VStack>
            <VStack space='1' flex={1} alignItems='flex-end'>
              <XText variant='inactive' fontSize='xs'>
                ยอดรวม
              </XText>
              <XText fontSize='sm' bold>
                {util.formatCurrency(item.net_amount)}
              </XText>
            </VStack>
          </HStack>

          {/* Updated at */}
          <HStack w='full' alignItems='center' space='1'>
            <XIcon name='time-outline' family='Ionicons' size='xs' color='muted.400' />
            <XText variant='inactive' fontSize='xs'>
              อัพเดท {updatedAtText}
            </XText>
          </HStack>

          {/* Action buttons or history list */}
          {hasQtyAdjustment ? (
            // Show qty adjustment history list
            <VStack w='full' pt='2' borderTopWidth={1} borderTopColor='muted.100' space='1'>
              <XText fontSize='xs' variant='inactive'>
                ประวัติการเติมสต๊อก
              </XText>
              {item.qty_adjustment_histories?.map((history: IErpDocQtyAdjustmentHistory) => (
                <TouchableOpacity key={history.id} onPress={() => this._navToQtyAdjustmentDetailView(history.id)}>
                  <HStack w='full' py='2' px='2' bg='muted.50' borderRadius='md' alignItems='center' justifyContent='space-between'>
                    <HStack alignItems='center' space='2'>
                      <XIcon name='document-text-outline' family='Ionicons' size='sm' color='muted.500' />
                      <XText fontSize='sm'>#{history.id}</XText>
                    </HStack>
                    <HStack alignItems='center' space='1'>
                      <XText fontSize='xs' variant='inactive'>
                        {history.created_at ? time.convertServerDateTimeToReadableDateTimeText(history.created_at) : '-'}
                      </XText>
                      <XIcon name='chevron-forward' family='Ionicons' size='sm' color='muted.400' />
                    </HStack>
                  </HStack>
                </TouchableOpacity>
              ))}
            </VStack>
          ) : (
            // Show add stock button
            <HStack w='full' pt='2' justifyContent='flex-end'>
              <XButton
                size='sm'
                onPress={() => this._onAddStock(item)}
                leftIcon={<XIcon name='add' family='Ionicons' color='white' />}
                colorScheme='success'
              >
                เติมสต๊อก
              </XButton>
            </HStack>
          )}
        </VStack>
      </XCard>
    )
  }

  _renderListFooter = () => {
    const { isFetchingMore, isReachEnd, documents } = this.state

    if (isFetchingMore) {
      return (
        <HStack w='full' p='4' justifyContent='center'>
          <XSpinner />
        </HStack>
      )
    }

    if (isReachEnd && documents.length > 0) {
      return (
        <HStack w='full' p='4' justifyContent='center'>
          <XText variant='inactive'>ไม่มีรายการเพิ่มเติม</XText>
        </HStack>
      )
    }

    return null
  }

  _renderEmptyList = () => {
    const { isRefreshing, tabIndex, fetchError } = this.state

    if (isRefreshing) {
      return null
    }

    // Show error state
    if (fetchError) {
      return (
        <VStack w='full' h='250px' alignItems='center' justifyContent='center' space='3' p='4'>
          <XIcon name='alert-circle-outline' family='Ionicons' size='3xl' color='red.400' />
          <XText color='red.500' textAlign='center'>
            เกิดข้อผิดพลาด
          </XText>
          <XText variant='inactive' textAlign='center' fontSize='sm'>
            {fetchError}
          </XText>
          <XButton onPress={() => this._onRefresh()} size='sm' colorScheme='primary' mt='2'>
            ลองใหม่อีกครั้ง
          </XButton>
        </VStack>
      )
    }

    const emptyMessage = tabIndex === 0 ? 'ไม่พบเอกสารที่ยังไม่เคยเติมสต๊อก' : 'ไม่พบเอกสารที่เคยเติมสต๊อกแล้ว'

    return (
      <VStack w='full' h='200px' alignItems='center' justifyContent='center'>
        <XIcon name='document-text-outline' family='Ionicons' size='2xl' color='muted.300' />
        <XText variant='inactive' mt='2'>
          {emptyMessage}
        </XText>
      </VStack>
    )
  }

  _renderTotalCount = () => {
    const { totalCount } = this.state

    return (
      <HStack w='full' px='3' py='2' bg='muted.100' borderBottomWidth={1} borderBottomColor='muted.200'>
        <XText variant='inactive' fontSize='sm'>
          {`ทั้งหมด ${totalCount} รายการ`}
        </XText>
      </HStack>
    )
  }

  render() {
    const { isInitialized, isRefreshing, documents, isSyncOverlayVisible } = this.state

    if (!isInitialized) {
      return (
        <XContainer>
          {this._renderCustomHeader()}
          <VStack flex={1} alignItems='center' justifyContent='center'>
            <XSpinner />
          </VStack>
        </XContainer>
      )
    }

    // Check if add stock feature is enabled
    // if (!this._isAddStockEnabled()) {
    //   return (
    //     <XContainer>
    //       {this._renderCustomHeader()}
    //       {this._renderNotEnabledMessage()}
    //     </XContainer>
    //   )
    // }

    return (
      <XContainer>
        {this._renderCustomHeader()}
        {this._renderSyncSection()}
        {this._renderSearchBar()}
        {this._renderTabs()}
        {this._renderDocTypeFilters()}
        {this._renderTotalCount()}
        <FlatList
          data={documents}
          keyExtractor={(item, index) => `doc_${item.erp_doc_id}_${index}`}
          renderItem={this._renderListItem}
          onEndReached={this._onEndReached}
          onEndReachedThreshold={0.3}
          ListFooterComponent={this._renderListFooter}
          ListEmptyComponent={this._renderEmptyList}
          refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={this._onRefresh} />}
        />
        <OverlaySetupSyncErpDocument
          isOverlayVisible={isSyncOverlayVisible}
          onClose={this._onCloseSyncOverlay}
          onSubmit={this._onSyncSubmit}
        />
      </XContainer>
    )
  }
}

export default connect(
  (state) => ({
    selectedStore: getSelectedStore(state),
  }),
  (dispatch) => ({ dispatch })
)(ErpAddStockFromDocView)
